import { APIClient } from "api/api_helper";
import { DASHBOARD_API, VOLUNTEER_API } from "api/url_helper";
import { CurrentUser, GetUserData } from "common/service/CommonService";
import {
  VolunteerFormValues,
  VolunteerSelectAllParams,
} from "modules/Volunteer/MasterVolunteer/helper/volunteer.types";

// ------------------------------------------------------------------
// API CLIENT
// ------------------------------------------------------------------ */

const api = new APIClient();

// ------------------------------------------------------------------
// Volunteer API
// ------------------------------------------------------------------

export const volunteerApiService = {
  // SELECT ALL METHOD
  getSelectAllData: (params: VolunteerSelectAllParams) => {
    const {
      otherid,
      volunteertype,
      searchText,
      pageNumber = 1,
      pageSize = 10,
      gender,
      smoker,
      min_age,
      max_age,
      minbmi,
      maxbmi,
      // alcohol,
      alcoholresult,
      bloodgroup,
      educationlevel,
      foodhabit,
      city,
      screeningstatus,
      screeningfromdate,
      screeningtodate,
      studyid
    } = params;

    const payload = {
      otherid,
      status: "A",
      searchtext: searchText,
      volunteertype: volunteertype,
      filters: {
        gender,
        smoker,
        minage: min_age,
        maxage: max_age,
        minbmi,
        maxbmi,
        // alcohol,
        alcoholresult,
        bloodgroup,
        educationlevel,
        foodhabit,
        city,
        screeningstatus,
        screeningfromdate,
        screeningtodate
      },
      currentpage: pageNumber,
      pagesize: pageSize,
      studyid:studyid,
    };

    return api.create(VOLUNTEER_API.VOLUNTEER_API + "SelectAll", payload);
  },

  getSelectData: (id: number) => {
    const data = {
      id: id,
    }
    return api.create(VOLUNTEER_API.VOLUNTEER_API + "Select",data);
  },

  // SELECT DASHBOARD METHOD
  getDashboardSelect: (volunteerid: number) => {
    const data = {
      volunteerid: volunteerid,
    }
    return api.create(sessionStorage.getItem("baseUrl") + "Volunteer/Dashboard/Select", data);
  },

  insertData: async (
    payload_data: VolunteerFormValues & {
      volunteerId?: number;
      updateReason?: string;
      acknowledged?: boolean;
      status?: string;
      files?: { file: File }[];
      deletedAttachmentIds?: number[];
      signature?: string | null;
    },
  ) => {
    const user = GetUserData() as CurrentUser;

    const formData = new FormData();

    /* =====================================
     Append Normal Fields
  ====================================== */

    Object.entries(payload_data).forEach(([key, value]) => {
      if (
        value === null ||
        value === undefined ||
        key === "files" ||
        key === "attachments" ||
        key === "deletedAttachmentIds"
      )
        return;

      // Select option object case { label, value }
      if (typeof value === "object" && value !== null && "value" in value) {
        formData.append(key, String((value as any).value));
      }
      // Boolean case
      else if (typeof value === "boolean") {
        formData.append(key, value ? "true" : "false");
      }
      // Normal case
      else {
        formData.append(key, String(value));
      }
    });

    /* =====================================
     Append Files (Binary)
  ====================================== */

    if (
      Array.isArray(payload_data.attachments) &&
      payload_data.attachments.length > 0
    ) {
      payload_data.attachments.forEach((fileObj) => {
        if (fileObj?.file instanceof File) {
          formData.append(`files[${fileObj.type}]`, fileObj.file);
        }
      });
    }

    /* =====================================
     Deleted Attachments
  ====================================== */

    formData.append(
      "deletedAttachmentIds",
      JSON.stringify(payload_data.deletedAttachmentIds ?? []),
    );

    /* =====================================
     Extra System Fields
  ====================================== */

    formData.append("createdby", String(user.userid));

    /* =====================================
     API Call
  ====================================== */

    return api.create(VOLUNTEER_API.VOLUNTEER_API + "Insert", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }
  
  );
  },

  updateData: (payload_data: VolunteerFormValues) => {
    const user = GetUserData() as CurrentUser;

    const formData = new FormData();

    /* =============================
     1. Exclude UI-Only Fields
  ============================= */

    Object.entries(payload_data).forEach(([key, value]) => {
      if (
        value === null ||
        value === undefined ||
        key === "files" ||
        key === "attachments" ||
        key === "alcoholtest"
      )
        return;

      // Select option object case { label, value }
      if (typeof value === "object" && value !== null && "value" in value) {
        formData.append(key, String((value as any).value));
      }
      // Boolean case
      else if (typeof value === "boolean") {
        formData.append(key, value ? "true" : "false");
      }
      // Normal case
      else {
        formData.append(key, String(value));
      }
    });

    /* =====================================
     Append Files (Binary)
  ====================================== */

    if (
      Array.isArray(payload_data.attachments) &&
      payload_data.attachments.length > 0
    ) {
      payload_data.attachments.forEach((fileObj) => {
        if (fileObj?.file instanceof File) {
          formData.append(`files[${fileObj.type}]`, fileObj.file);
        }
      });
    }

    /* =====================================
     Deleted Attachments
  ====================================== */
    // formData.append(
    //   "deletedAttachmentIds",
    //   JSON.stringify(payload_data.deletedAttachmentIds ?? []),
    // );
    /* =============================
     5. Audit Fields
  ============================= */

    formData.append("updatedby", String(user.userid));
    formData.append("createdby", String(user.userid));

    return api.create(VOLUNTEER_API.VOLUNTEER_API + "Update", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  },

  // DELETE METHOD
  deleteData: (payload_data: {
    volunteerid: number | null;
    updatereason: string;
  }) => {
    const user = GetUserData() as CurrentUser;
    const payload = {
      volunteerid: payload_data.volunteerid,
      updatereason: payload_data.updatereason,
      updatedby: user.userid,
    };
    return api.create(VOLUNTEER_API.VOLUNTEER_API + "Delete", payload);
  },

  // VERIFY METHOD
  verifyData: (payload_data: {
    volunteerid: number;
    verifypin: string;
  }) => {
    const user = GetUserData() as CurrentUser;
    const payload = {
      volunteerid: payload_data.volunteerid,
      verifypin: payload_data.verifypin,
      verifiedby: user.userid,
    };
    return api.create(VOLUNTEER_API.VOLUNTEER_API + "Verify", payload);
  },

  // DROPDOWN METHOD
  getDropdownData: () => {
    const payload = {
      status: "A",
    
    };
    return api.create(VOLUNTEER_API.VOLUNTEER_API + "Dropdown", payload);
  },
};
